/**
 * Portfolio API
 *
 * Mock API for portfolio operations.
 */

import { success, error, withDelay, generateId, timestamp, delay } from './index'
import type { ApiResponse } from '../types'
import type { Portfolio } from '../types/portfolio.types'

const mockPortfolios: Portfolio[] = [
  {
    id: 'port-001',
    title: 'My Outdoor Adventures',
    introduction: 'A collection of my favorite outdoor challenges from the past three months.',
    sections: [
      { title: 'Nature Exploration', trackRecordIds: ['tr-001', 'tr-002'], description: 'Getting into nature' },
      { title: 'Cooking Adventures', trackRecordIds: ['tr-003'], description: 'Kitchen experiments' },
    ],
    templateId: 'classic',
    status: 'published',
    shareSettings: { linkType: 'public' },
    viewCount: 23,
    userId: 'demo-user-001',
    createdAt: '2024-03-01T10:00:00Z',
    updatedAt: '2024-03-10T10:00:00Z',
  },
  {
    id: 'port-002',
    title: 'Science Projects Portfolio',
    introduction: 'Documenting my science journey through DoCurious challenges.',
    sections: [
      { title: 'Experiments', trackRecordIds: ['tr-004', 'tr-005'] },
    ],
    templateId: 'minimal',
    status: 'draft',
    shareSettings: { linkType: 'private' },
    viewCount: 0,
    userId: 'demo-user-001',
    createdAt: '2024-03-12T10:00:00Z',
    updatedAt: '2024-03-12T10:00:00Z',
  },
]

export const portfolioApi = {
  async getPortfolios(userId: string): Promise<ApiResponse<Portfolio[]>> {
    return withDelay(() => {
      const portfolios = mockPortfolios.filter(p => p.userId === userId)
      return success(portfolios)
    })
  },

  async getPortfolioById(id: string): Promise<ApiResponse<Portfolio>> {
    return withDelay(() => {
      const portfolio = mockPortfolios.find(p => p.id === id)
      if (!portfolio) return error('Portfolio not found')
      return success(portfolio)
    })
  },

  async createPortfolio(data: Partial<Portfolio> & { userId: string }): Promise<ApiResponse<Portfolio>> {
    return withDelay(() => {
      const portfolio: Portfolio = {
        id: generateId(),
        title: data.title ?? 'Untitled Portfolio',
        introduction: data.introduction,
        sections: data.sections ?? [],
        templateId: data.templateId ?? 'classic',
        status: 'draft',
        shareSettings: { linkType: 'private' },
        viewCount: 0,
        userId: data.userId,
        createdAt: timestamp(),
        updatedAt: timestamp(),
      }
      mockPortfolios.push(portfolio)
      return success(portfolio)
    })
  },

  async updatePortfolio(id: string, data: Partial<Portfolio>): Promise<ApiResponse<Portfolio>> {
    return withDelay(() => {
      const portfolio = mockPortfolios.find(p => p.id === id)
      if (!portfolio) return error('Portfolio not found')
      Object.assign(portfolio, data, { updatedAt: timestamp() })
      return success(portfolio)
    })
  },

  async deletePortfolio(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return withDelay(() => {
      const idx = mockPortfolios.findIndex(p => p.id === id)
      if (idx === -1) return error('Portfolio not found')
      mockPortfolios.splice(idx, 1)
      return success({ deleted: true })
    })
  },

  async generateShareLink(portfolioId: string): Promise<ApiResponse<{ token: string, url: string }>> {
    await delay(300)
    const token = `share-${portfolioId}-${Date.now().toString(36)}`
    return success({ token, url: `/portfolio/shared/${token}` })
  },

  async getPortfolioByToken(token: string): Promise<ApiResponse<Portfolio>> {
    await delay(300)
    void token
    // Mock: return first portfolio
    if (mockPortfolios.length > 0) return success(mockPortfolios[0])
    return error('Portfolio not found')
  },
}
